import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { __ } from '@wordpress/i18n';
import { getFieldGroupInfo, getIconForType } from '../utils/fieldTypes';
import { presetFields } from '../utils/presetFields';
import './Field.css';

const Field = ({ 
  id, 
  field, 
  index, 
  fieldsLength, 
  onEdit, 
  onDelete, 
  onToggle, 
  onToggleRequired,
  onToggleAdminOnly,
  onMoveUp, 
  onMoveDown,
  showToast,
  isFieldLocked,
  isGroupField = false,
  groupPosition,
  groupTotal
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: field.id,
    data: {
      type: 'field',
      fieldId: field.id,
      fieldType: field.type,
      isGroupField: isGroupField || !!field.group_id,
      groupId: field.group_id
    },
    disabled: isFieldLocked // Only disable for locked fields, NOT for group fields
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 100 : 1,
    boxShadow: isDragging ? 'var(--hvnly-card-shadow)' : 'none',
  };
  
  const isPresetField = () => {
    return Object.values(presetFields).some(category => 
      category.fields.some(presetField => field.id && field.id.startsWith(presetField.id))
    );
  };
  
  const getPresetFieldInfo = () => {
    if (!isPresetField()) return null;
    
    for (const category of Object.values(presetFields)) {
      for (const presetField of category.fields) {
        if (field.id && field.id.startsWith(presetField.id)) {
          return {
            category: category.name,
            icon: presetField.icon,
            originalLabel: presetField.defaultLabel
          };
        }
      }
    }
    return null;
  };
  
  const presetInfo = getPresetFieldInfo();
  const groupInfo = getFieldGroupInfo(field);
  
  // Determine if this field is part of a group
  const isPartOfGroup = isGroupField || !!field.group_id || groupInfo !== null;
  
  // Use provided groupPosition/groupTotal or get from field/groupInfo
  const currentGroupPosition = groupPosition !== undefined ? groupPosition : 
                              (field.group_position !== undefined ? field.group_position : 
                              (groupInfo?.position !== undefined ? groupInfo.position : 0));
  
  const currentGroupTotal = groupTotal !== undefined ? groupTotal :
                           (field.group_total !== undefined ? field.group_total :
                           (groupInfo?.total !== undefined ? groupInfo.total : 0));
  
  const currentIndex = field.order !== undefined ? field.order : index;
  const canMoveUp = !isFieldLocked && currentIndex > 0;
  const canMoveDown = !isFieldLocked && currentIndex < fieldsLength - 1;
  
  const handleDelete = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Delete', 'havenlytics'), __('This field is required and cannot be deleted', 'havenlytics'));
      return;
    }
    
    // FIXED: Allow deletion of individual group fields
    onDelete();
  };
  
  const handleEdit = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Edit', 'havenlytics'), __('This field is required and cannot be edited', 'havenlytics'));
      return;
    }
    
    // FIXED: Allow editing of individual group fields
    onEdit();
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Modify', 'havenlytics'), __('This field is required and cannot be modified', 'havenlytics'));
      return;
    }
    
    onToggle();
  };

  const handleToggleRequired = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Modify', 'havenlytics'), __('This field is required and cannot be modified', 'havenlytics'));
      return;
    }
    
    onToggleRequired();
  };

  const handleToggleAdminOnly = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Modify', 'havenlytics'), __('This field is required and cannot be modified', 'havenlytics'));
      return;
    }
    
    onToggleAdminOnly();
  };

  const handleMoveUp = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Move', 'havenlytics'), __('This field is required and cannot be moved', 'havenlytics'));
      return;
    }
    
    // FIXED: Allow moving of individual group fields
    onMoveUp();
  };

  const handleMoveDown = (e) => {
    e.stopPropagation();
    if (isFieldLocked) {
      showToast('error', __('Cannot Move', 'havenlytics'), __('This field is required and cannot be moved', 'havenlytics'));
      return;
    }
    
    onMoveDown();
  };
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`hvnly--builder--int_api__property_builder_field 
        ${isFieldLocked ? 'hvnly--builder--int_api__locked_field' : ''} 
        ${isPresetField() ? 'hvnly--builder--int_api__preset_field' : ''} 
        ${isPartOfGroup ? 'hvnly--builder--int_api__group_field' : ''} 
        ${isDragging ? 'dragging' : ''}`}
      data-field-id={field.id}
      data-field-order={currentIndex}
      data-group-id={groupInfo?.id || field.group_id}
    >
      <div 
        className="hvnly--builder--int_api__drag_handle"
        {...attributes}
        {...listeners}
        style={{ 
          cursor: isFieldLocked ? 'not-allowed' : 'grab',
          opacity: isFieldLocked ? 0.5 : 1
        }}
        title={isFieldLocked ? __('Required field cannot be moved', 'havenlytics') : __('Drag to reorder', 'havenlytics')}
      >
        <i className={`fas fa-${isFieldLocked ? 'lock' : 'grip-vertical'}`}></i>
      </div>
      
      {isPartOfGroup && currentGroupTotal > 0 && (
        <div className="hvnly--builder--int_api__group_position_indicator">
          <span className="hvnly--builder--int_api__group_position_number">
            {currentGroupPosition + 1}
          </span>
          <span className="hvnly--builder--int_api__group_position_total">
            /{currentGroupTotal}
          </span>
        </div>
      )}
      
      <div className="hvnly--builder--int_api__property_builder_field_info">
        <i className={`fas fa-${presetInfo ? presetInfo.icon : getIconForType(field.type)}`}></i>
        <div className="hvnly--builder--int_api__field_details">
          <div className="hvnly--builder--int_api__field_label">
            {field.label || field.name || __('Untitled Field', 'havenlytics')}
            {isFieldLocked && (
              <span className="hvnly--builder--int_api__locked_badge">
                <i className="fas fa-lock"></i> {__('Required', 'havenlytics')}
              </span>
            )}
            {isPresetField() && (
              <span className="hvnly--builder--int_api__preset_badge">
                <i className="fas fa-star"></i> {__('Preset', 'havenlytics')}
              </span>
            )}
            {isPartOfGroup && (
              <span className="hvnly--builder--int_api__group_badge">
                <i className={`fas fa-${groupInfo?.icon || 'folder'}`}></i> 
                {groupInfo?.name || field.group_name || __('Group Field', 'havenlytics')}
              </span>
            )}
          </div>
          <div className="hvnly--builder--int_api__field_meta">
            <span className="hvnly--builder--int_api__field_type">
              <i className={`fas fa-${getIconForType(field.type)}`}></i> 
              {field.type}
            </span>
            {field.placeholder && (
              <span className="hvnly--builder--int_api__field_placeholder">
                <i className="fas fa-text"></i> {__('Placeholder:', 'havenlytics')} {field.placeholder}
              </span>
            )}
            {presetInfo && (
              <span className="hvnly--builder--int_api__field_category">
                <i className="fas fa-folder"></i> {presetInfo.category}
              </span>
            )}
            {isPartOfGroup && currentGroupTotal > 0 && (
              <span className="hvnly--builder--int_api__group_position_text">
                <i className="fas fa-hashtag"></i> {__('Position', 'havenlytics')} {currentGroupPosition + 1} {__('of', 'havenlytics')} {currentGroupTotal}
              </span>
            )}
            {field.required && !isFieldLocked && (
              <span className="hvnly--builder--int_api__field_required_badge">
                <i className="fas fa-asterisk"></i> {__('Required', 'havenlytics')}
              </span>
            )}
            {field.adminOnly && (
              <span className="hvnly--builder--int_api__field_admin_badge">
                <i className="fas fa-user-shield"></i> {__('Admin Only', 'havenlytics')}
              </span>
            )}
            {!field.enabled && field.enabled !== undefined && (
              <span className="hvnly--builder--int_api__field_disabled_badge">
                <i className="fas fa-eye-slash"></i> {__('Disabled', 'havenlytics')}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="hvnly--builder--int_api__property_builder_field_actions">
        {!isFieldLocked ? (
          <>
            <button
              className={`hvnly--builder--int_api__property_builder_field_action_btn toggle-btn ${!field.enabled ? 'hvnly--builder--int_api__property_builder_field_action_btn_active' : ''}`}
              onClick={handleToggle}
              title={field.enabled ? __('Disable Field', 'havenlytics') : __('Enable Field', 'havenlytics')}
            >
              <i className={field.enabled ? 'fas fa-toggle-on' : 'fas fa-toggle-off'}></i>
            </button>
            
            <button
              className={`hvnly--builder--int_api__property_builder_field_action_btn required-btn ${field.required ? 'hvnly--builder--int_api__property_builder_field_action_btn_active' : ''}`}
              onClick={handleToggleRequired}
              title={field.required ? __('Make Optional', 'havenlytics') : __('Make Required', 'havenlytics')}
            >
              <i className={`fas fa-${field.required ? 'check-circle' : 'circle'}`}></i>
            </button>
            
            <button
              className={`hvnly--builder--int_api__property_builder_field_action_btn admin-btn ${field.adminOnly ? 'hvnly--builder--int_api__property_builder_field_action_btn_active' : ''}`}
              onClick={handleToggleAdminOnly}
              title={field.adminOnly ? __('Make Public', 'havenlytics') : __('Make Admin Only', 'havenlytics')}
            >
              <i className={`fas fa-${field.adminOnly ? 'user-shield' : 'user'}`}></i>
            </button>

            <>
              <button
                className={`hvnly--builder--int_api__property_builder_field_action_btn move-up-btn ${!canMoveUp ? 'hvnly--builder--int_api__disabled_action_btn' : ''}`}
                onClick={handleMoveUp}
                disabled={!canMoveUp}
                title={canMoveUp ? __("Move Field Up", "havenlytics") : __("Already at top", "havenlytics")}
              >
                <i className="fas fa-arrow-up"></i>
              </button>

              <button
                className={`hvnly--builder--int_api__property_builder_field_action_btn move-down-btn ${!canMoveDown ? 'hvnly--builder--int_api__disabled_action_btn' : ''}`}
                onClick={handleMoveDown}
                disabled={!canMoveDown}
                title={canMoveDown ? __("Move Field Down", "havenlytics") : __("Already at bottom", "havenlytics")}
              >
                <i className="fas fa-arrow-down"></i>
              </button>
            </>
            
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn edit-btn"
              onClick={handleEdit}
              title={__("Edit Field", "havenlytics")}
            >
              <i className="fas fa-edit"></i>
            </button>
            
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn delete-btn"
              onClick={handleDelete}
              title={__("Delete Field", "havenlytics")}
            >
              <i className="fas fa-trash"></i>
            </button>
          </>
        ) : (
          <>
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn toggle-btn hvnly--builder--int_api__locked_action_btn"
              title={__("Required Field - Cannot be disabled", "havenlytics")}
              disabled
            >
              <i className="fas fa-toggle-on"></i>
            </button>
            
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn required-btn hvnly--builder--int_api__locked_action_btn"
              title={__("Required Field - Always required", "havenlytics")}
              disabled
            >
              <i className="fas fa-check-circle"></i>
            </button>
            
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn admin-btn hvnly--builder--int_api__locked_action_btn"
              title={__("Required Field - Admin only setting locked", "havenlytics")}
              disabled
            >
              <i className="fas fa-user-shield"></i>
            </button>
            
            <>
              <button
                className="hvnly--builder--int_api__property_builder_field_action_btn move-up-btn hvnly--builder--int_api__locked_action_btn"
                title={__("Required Field - Cannot be moved", "havenlytics")}
                disabled
              >
                <i className="fas fa-arrow-up"></i>
              </button>
              
              <button
                className="hvnly--builder--int_api__property_builder_field_action_btn move-down-btn hvnly--builder--int_api__locked_action_btn"
                title={__("Required Field - Cannot be moved", "havenlytics")}
                disabled
              >
                <i className="fas fa-arrow-down"></i>
              </button>
            </>
            
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn edit-btn hvnly--builder--int_api__locked_action_btn"
              title={__("Required Field - Cannot be edited", "havenlytics")}
              disabled
            >
              <i className="fas fa-edit"></i>
            </button>
            
            <button
              className="hvnly--builder--int_api__property_builder_field_action_btn delete-btn hvnly--builder--int_api__locked_action_btn"
              title={__("Required Field - Cannot be deleted", "havenlytics")}
              disabled
            >
              <i className="fas fa-trash"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Field;